import { BASE_URL } from "@src/helpers/constants";
import {
  AppDispatch,
  exportNcdRiskReadingList,
  exportNcdRiskReadingListClear,
  exportNcdRiskReadingListRequest,
  useDynamicSelector,
} from "@src/redux";
import { Button, Input, message, Modal, Tag } from "antd";
import { snakeCase } from "lodash";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

interface ReadingsReportModalProps {
  isModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  ncdRiskType?: string;
}

const NcdReadingsReportExportModal: React.FC<ReadingsReportModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  ncdRiskType,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>("");
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { ncdRiskFilter } = useDynamicSelector("ncd_risk_analysis");
  const { id: subjectId } = useParams();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const { result: excelExport, loading } = useSelector(
    exportNcdRiskReadingList
  );

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (emailRegex.test(trimmedEmail)) {
      if (!emails.includes(trimmedEmail)) {
        setEmails([...emails, trimmedEmail]);
        setEmailInput("");
      } else {
        message.warning(t("email_already_added"));
      }
    } else {
      message.error(t("invalid_email"));
    }
  };

  const handleRemoveEmail = (removedEmail: string) => {
    setEmails(emails.filter((email) => email !== removedEmail));
  };

  const isValidEmail = (email: string) => emailRegex.test(email.trim());


  const handleExportReadingReport = () => {

    dispatch(
      exportNcdRiskReadingListRequest({
        filter: {
          organizationId: ncdRiskFilter?.organizationId,
          fromDate: ncdRiskFilter?.fromDate,
          toDate: ncdRiskFilter?.toDate,
          fromAge: ncdRiskFilter?.fromAge,
          toAge: ncdRiskFilter?.toAge,
          gender: ncdRiskFilter?.gender,
          ncdConfirmationType: ncdRiskFilter?.ncdConfirmationType || "",
          levelAttributeValueList: ncdRiskFilter?.levelAttributeValueList,
          riskType: ncdRiskType || "",
        },
        data: {
          emailIdList:
            emails?.length > 0
              ? emails
              : emailInput
              ? [emailInput]
              : [],
        },
      })
    );
  };

  useEffect(() => {
    if (excelExport?.status === "success") {
      dispatch(exportNcdRiskReadingListClear());
      setIsModalVisible(false);

      setEmails([]);
      toast.success(
        t(
          "Your email request has been created. You will receive the report over email within 15minutes."
        )
      );
    } else if (excelExport?.error?.message) {
      const serverMessage = excelExport.error.message;
      dispatch(exportNcdRiskReadingListClear());
      // Show what the server actually said. These messages are written for
      // end users ("storage is not configured... contact support", "the
      // report worker could not be started"), and collapsing them all into
      // "Something went wrong" left users and support with no way to tell an
      // outage from a misconfiguration. Fall back to the generic text only
      // for messages that are not user-facing.
      toast.error(
        serverMessage && serverMessage.length <= 300
          ? t(serverMessage)
          : t("Something went wrong")
      );
    }
  }, [excelExport]);
  return (
    <Modal
      title={
        <span className="reading-modal-title">{t("reading_modal_title")}</span>
      }
      visible={isModalVisible}
      onCancel={handleCancel}
      footer={null}
    >
      <div>
        <div style={{ display: "flex" }}>
          <Input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onPressEnter={handleAddEmail}
            placeholder={t("enter_emails")}
          />
          <Button
            type="primary"
            style={{ marginLeft: "10px" }}
            disabled={emailInput ? !isValidEmail(emailInput) : false}
            onClick={handleExportReadingReport}
            loading={loading}
          >
            {t("email")}
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginTop: "5px",
          }}
        >
          {emails?.map((email) => (
            <Tag
              key={email}
              closable
              onClose={() => handleRemoveEmail(email)}
              style={{ backgroundColor: "#319795", color: "#fff" }}
              className="mail-clear-tag"
            >
              {email}
            </Tag>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default NcdReadingsReportExportModal;
